import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/modal';
import Card from 'components/card';
import DateRangePicker from 'components/dataPicker';
import SearchSelector from 'components/fields/SearchSelector';
import { set } from 'date-fns';
import { getCustomers, searchCustomersForSelect } from 'interfaces/customer';
import { downloadSimpleInvoiceExport, downloadSepaXml } from 'interfaces/invoice';
import { useEffect, useState } from 'react';

const SimpleInvoiceExportModal = (props: {
    isOpen: any;
    onClose: any;
    handleExport?: any;
    archived?: boolean;
}) => {
    const { isOpen, onClose } = props;

    const [filter, setFilter] = useState({
        startDate: new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000),
        endDate: new Date(),
        status: 'all',
        customerPaymentMethod: 'all',
        customerId: 'all',
    });
    const [customers, setCustomers] = useState<any>([]);
    const [loading, setLoading] = useState(false);

    const defaultCollectionDate = new Date(new Date().getTime() + 5 * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10);
    const minCollectionDate = new Date(new Date().getTime() + 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10);

    const [sepaOptions, setSepaOptions] = useState({
        requestedCollectionDate: defaultCollectionDate,
        sequenceType: 'RCUR',
    });

    const handleDateChange = (dates: any) => {
        const startDate = dates.startDate;
        const endDate = dates.endDate;
        setFilter((prevFilter: any) => ({
            ...prevFilter,
            startDate,
            endDate,
        }));
    };

    const initCustomers = async () => {
        const res = await getCustomers();
        if (res) setCustomers(res.data);
    };

    const getDateRange = () => {
        const start = set(filter.startDate, { hours: 0, minutes: 0, seconds: 0 }).toISOString();
        const end = set(filter.endDate, { hours: 23, minutes: 59, seconds: 59 }).toISOString();
        return { start, end };
    };

    const handleExport = async () => {
        const { start, end } = getDateRange();
        try {
            setLoading(true);
            const blob = await downloadSimpleInvoiceExport(
                start,
                end,
                filter.status,
                filter.customerPaymentMethod,
                filter.customerId,
            );
            if (!blob) return;
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `export-simple-${new Date().toLocaleDateString()}.csv`;
            link.click();
            onClose();
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExportSepaXml = async () => {
        const { start, end } = getDateRange();
        try {
            setLoading(true);
            const blob = await downloadSepaXml(
                start,
                end,
                sepaOptions.requestedCollectionDate,
                sepaOptions.sequenceType,
            );
            if (!blob) return;
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `prelevement-sepa-${new Date().toLocaleDateString()}.xml`;
            link.click();
            onClose();
        } catch (error) {
            console.error('Erreur export Prélèvement SEPA:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        initCustomers();
    }, []);

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="!z-[1002] !m-auto !w-max min-w-[350px] !max-w-[85%] md:top-[12vh]">
                <ModalBody>
                    <Card extra="px-[30px] pt-[35px] pb-[40px] max-w-[650px] flex flex-col !z-[1004]">
                        <h1 className="text-xl font-bold">
                            Export des factures
                        </h1>
                        <p className="mt-3 text-sm">
                            💡 Cet export vous permet de récupérer vos factures
                            au format CSV.
                        </p>
                        <div className="grid grid-cols-2 mt-5 gap-5">
                            <div className="w-full">
                                <DateRangePicker
                                    label="Dates de facturation*"
                                    defaultStartDate={filter.startDate}
                                    defaultEndDate={filter.endDate}
                                    onChange={handleDateChange}
                                />
                            </div>
                            <SearchSelector
                                label="Client*"
                                displayKey="company"
                                options={[
                                    {
                                        _id: 'all',
                                        company: 'Tous',
                                    },
                                    ...customers.map((customer: any) => ({
                                        _id: customer._id,
                                        company:
                                            customer.displayName ||
                                            customer.company,
                                    })),
                                ]}
                                onSearch={searchCustomersForSelect}
                                value={filter?.customerId}
                                onSelect={(e: any) => {
                                    setFilter((prevFilter: any) => ({
                                        ...prevFilter,
                                        customerId: e._id,
                                    }));
                                }}
                                inputSmall={true}
                            />
                            <SearchSelector
                                label="Statut des factures*"
                                displayKey="name"
                                inputSmall={true}
                                value={filter.status}
                                options={[
                                    { name: 'Tous', _id: 'all' },
                                    {
                                        name: 'Non envoyée',
                                        _id: 'generated',
                                    },
                                    {
                                        name: 'Envoyée',
                                        _id: 'sent',
                                    },
                                    { name: 'Payée', _id: 'paid' },
                                    {
                                        name: 'Avoir',
                                        _id: 'credit-note',
                                    },
                                    { name: 'Annulée', _id: 'canceled' },
                                ]}
                                onSelect={(e: any) => {
                                    setFilter((prevFilter: any) => ({
                                        ...prevFilter,
                                        status: e._id,
                                    }));
                                }}
                            />
                            <SearchSelector
                                label="Mode de facturation du client*"
                                displayKey="name"
                                inputSmall={true}
                                value={filter.customerPaymentMethod}
                                options={[
                                    {
                                        _id: 'all',
                                        name: 'Tous',
                                    },
                                    {
                                        _id: 'cash',
                                        name: 'Espèces',
                                    },
                                    {
                                        _id: 'check',
                                        name: 'Chèque',
                                    },
                                    {
                                        _id: 'transfer',
                                        name: 'Virement',
                                    },
                                    {
                                        _id: 'credit-card',
                                        name: 'Carte bancaire',
                                    },
                                    {
                                        _id: 'direct-debit',
                                        name: 'Prélèvement',
                                    },
                                ]}
                                onSelect={(e: any) => {
                                    setFilter((prevFilter: any) => ({
                                        ...prevFilter,
                                        customerPaymentMethod: e._id,
                                    }));
                                }}
                            />
                        </div>

                        <div className="mt-5">
                            <p className="text-xs font-medium text-gray-600">
                                Options du Prélèvement SEPA
                            </p>
                            <div className="flex gap-4 mt-2">
                                <div>
                                    <label className="text-xs font-medium text-gray-600">
                                        Date d'échéance*
                                    </label>
                                    <input
                                        type="date"
                                        value={sepaOptions.requestedCollectionDate}
                                        min={minCollectionDate}
                                        onChange={(e: any) => {
                                            setSepaOptions((prev: any) => ({
                                                ...prev,
                                                requestedCollectionDate: e.target.value,
                                            }));
                                        }}
                                        className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-navy-700 focus:outline-none focus:ring-2 focus:ring-toola-500"
                                    />
                                </div>
                                <div className="w-[220px]">
                                    <SearchSelector
                                        label="Type de séquence"
                                        displayKey="name"
                                        inputSmall={true}
                                        value={sepaOptions.sequenceType}
                                        options={[
                                            { _id: 'RCUR', name: 'Récurrent (RCUR)' },
                                            { _id: 'FRST', name: '1er prélèvement (FRST)' },
                                            { _id: 'FNAL', name: 'Dernier de la série (FNAL)' },
                                            { _id: 'OOFF', name: 'Ponctuel (OOFF)' },
                                        ]}
                                        onSelect={(e: any) => {
                                            setSepaOptions((prev: any) => ({
                                                ...prev,
                                                sequenceType: e._id,
                                            }));
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mt-10 flex flex-wrap gap-2">
                            <button
                                onClick={onClose}
                                className="linear rounded-xl border-2 border-gray-700 px-5 py-3 text-base font-medium text-gray-700 transition duration-200 hover:bg-red-600/5 active:bg-red-700/5 dark:border-red-400 dark:bg-red-400/10 dark:text-white dark:hover:bg-red-300/10 dark:active:bg-red-200/10"
                            >
                                Annuler
                            </button>
                            <button
                                disabled={loading}
                                className="linear col-span-12 ml-3 flex items-center justify-center rounded-xl bg-toola-500 px-3 py-3 text-xs font-medium text-white hover:bg-toola-600 active:bg-toola-700 sm:col-span-6 lg:col-span-4"
                                onClick={handleExport}
                            >
                                {loading ? 'Export...' : 'Exporter les factures'}
                            </button>
                            <button
                                disabled={loading}
                                className="linear col-span-12 ml-3 flex items-center justify-center rounded-xl bg-purple-600 px-3 py-3 text-xs font-medium text-white hover:bg-purple-700 active:bg-purple-800 sm:col-span-6 lg:col-span-4"
                                onClick={handleExportSepaXml}
                            >
                                {loading ? 'Export...' : 'Export Prélèvement SEPA'}
                            </button>
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default SimpleInvoiceExportModal;
